import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import productData from "../Datas/productData";
import {
  formatPrice,
  getProductPrice,
  getProductVariant,
} from "../Utils/product";
import { CartContext } from "./CartContext";
import ProductImage from "./ProductImage";
import "../css/productdetails.scss";

const asArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const displayValue = (value) =>
  asArray(value)
    .filter(Boolean)
    .join(", ");

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useContext(CartContext);
  const product = productData.find(
    (item) => String(item.id) === String(id) || item.slug === id
  );
  const [selectedVariantLabel, setSelectedVariantLabel] = useState("");
  const [selectedPackaging, setSelectedPackaging] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    setSelectedVariantLabel(product?.variants?.[0]?.label || "");
    setSelectedPackaging(product?.packaging?.[0] || "");
    setQuantity(1);
    setAddedToCart(false);
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];

    const collections = new Set(asArray(product.collections));
    return productData
      .filter((candidate) => {
        if (candidate.id === product.id) return false;
        if (
          candidate.categorySlug &&
          candidate.categorySlug === product.categorySlug
        ) {
          return true;
        }
        return asArray(candidate.collections).some((item) =>
          collections.has(item)
        );
      })
      .sort(
        (a, b) =>
          Number(b.featured) - Number(a.featured) ||
          Number(b.rating || 0) - Number(a.rating || 0)
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found" id="main-content">
        <div className="product-detail__not-found">
          <p className="product-detail__eyebrow">Product unavailable</p>
          <h1>We could not find that ingredient.</h1>
          <p>
            It may have moved or be temporarily unavailable. Browse the full
            collection to discover another pantry favorite.
          </p>
          <Link to="/shop">Back to Shop</Link>
        </div>
      </main>
    );
  }

  const variants = asArray(product.variants);
  const packaging = asArray(product.packaging);
  const descriptions = asArray(product.description);
  const features = asArray(product.Features);
  const selectedVariant = getProductVariant(product, selectedVariantLabel);
  const currentPrice = getProductPrice(product, selectedVariantLabel);
  const reviewCount = Number(product.reviews) || 0;
  const numericRating = Number(product.rating) || 0;
  const starRating = Math.max(0, Math.min(5, Math.round(numericRating)));
  const categoryHref = product.categorySlug
    ? `/shop?category=${encodeURIComponent(product.categorySlug)}`
    : "/shop";
  const isOnSale = Number(product.compareAtPrice) > currentPrice;

  const informationRows = [
    ["SKU", product.sku],
    ["Origin", product.origin],
    ["Taste & Aroma", product.taste],
    ["Heat Level", product.heat],
    ["Ingredients", displayValue(product.ingredients)],
    ["Dietary Preferences", displayValue(product.dietary)],
    ["Available Packaging", displayValue(product.packaging)],
  ].filter(([, value]) => value);

  const changeQuantity = (nextQuantity) => {
    const normalizedQuantity = Math.max(
      1,
      Math.min(99, Number(nextQuantity) || 1)
    );
    setQuantity(normalizedQuantity);
    setAddedToCart(false);
  };

  const handleAddToCart = (event) => {
    event.preventDefault();
    if (!product.inStock) return;

    addToCart(
      product.id,
      quantity,
      selectedVariant?.label || selectedVariantLabel,
      selectedPackaging
    );
    setAddedToCart(true);
  };

  return (
    <main className="product-detail main" id="main-content">
      <div className="product-detail__shell">
        <nav className="product-detail__breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to={categoryHref}>{product.category}</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{product.name}</span>
        </nav>

        <article className="product-detail__overview">
          <div className="product-detail__media">
            {product.label && (
              <span className="product-detail__badge">{product.label}</span>
            )}
            <ProductImage
              product={product}
              className="product-detail__image"
              ariaLabel={product.name}
            />
            <p className="product-detail__image-note">
              Packed for freshness and photographed to show natural color and
              texture.
            </p>
          </div>

          <section className="product-detail__buy-box">
            <p className="product-detail__brand">
              {product.brand || "sickzuno"}
            </p>
            <h1>{product.name}</h1>

            <div className="product-detail__rating-row">
              <span
                className="product-detail__stars"
                aria-label={`${numericRating} out of 5 stars`}
              >
                <span aria-hidden="true">
                  {"★".repeat(starRating)}
                  {"☆".repeat(5 - starRating)}
                </span>
              </span>
              <span>
                {numericRating.toFixed(1)} ({reviewCount} Reviews)
              </span>
            </div>

            <div className="product-detail__stock-row">
              <span
                className={
                  product.inStock
                    ? "product-detail__stock is-in-stock"
                    : "product-detail__stock is-out-of-stock"
                }
              >
                {product.inStock ? "In stock" : "Out of stock"}
              </span>
              <span>SKU: {product.sku}</span>
            </div>

            <div className="product-detail__price" aria-live="polite">
              <strong>{formatPrice(currentPrice)}</strong>
              {isOnSale && <del>{formatPrice(product.compareAtPrice)}</del>}
              {selectedVariant?.label && <small>{selectedVariant.label}</small>}
            </div>

            {descriptions[0] && (
              <p className="product-detail__lead">{descriptions[0]}</p>
            )}

            <form className="product-detail__form" onSubmit={handleAddToCart}>
              {variants.length > 0 && (
                <fieldset className="product-detail__variants">
                  <legend>Choose a size</legend>
                  <div className="product-detail__variant-grid">
                    {variants.map((variant) => (
                      <label
                        className={
                          selectedVariantLabel === variant.label
                            ? "is-selected"
                            : ""
                        }
                        key={variant.label}
                      >
                        <input
                          type="radio"
                          name="product-variant"
                          value={variant.label}
                          checked={selectedVariantLabel === variant.label}
                          onChange={() => {
                            setSelectedVariantLabel(variant.label);
                            setAddedToCart(false);
                          }}
                        />
                        <span>
                          <strong>{variant.label}</strong>
                          <small>{formatPrice(variant.price)}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {packaging.length > 0 && (
                <label className="product-detail__select">
                  <span>Packaging</span>
                  <select
                    value={selectedPackaging}
                    onChange={(event) => {
                      setSelectedPackaging(event.target.value);
                      setAddedToCart(false);
                    }}
                  >
                    {packaging.map((option) => (
                      <option value={option} key={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="product-detail__actions">
                <div className="product-detail__quantity">
                  <span id="quantity-label">Qty</span>
                  <div>
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      disabled={quantity <= 1}
                      onClick={() => changeQuantity(quantity - 1)}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      inputMode="numeric"
                      value={quantity}
                      aria-labelledby="quantity-label"
                      onChange={(event) => changeQuantity(event.target.value)}
                    />
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      disabled={quantity >= 99}
                      onClick={() => changeQuantity(quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                <button
                  className="product-detail__add-button"
                  type="submit"
                  disabled={!product.inStock}
                >
                  {product.inStock ? "Add to Cart" : "Currently Unavailable"}
                </button>
              </div>
            </form>

            <div
              className={`product-detail__cart-message${
                addedToCart ? " is-visible" : ""
              }`}
              aria-live="polite"
            >
              {addedToCart && (
                <>
                  <span>Added to your cart.</span>
                  <Link to="/cartpage">View Cart</Link>
                </>
              )}
            </div>

            <ul className="product-detail__service-list">
              <li>
                <strong>Tracked shipping</strong>
                <span>Flat $9.99 delivery fee</span>
              </li>
              <li>
                <strong>Spicy satisfaction</strong>
                <span>Quality ingredients, carefully packed</span>
              </li>
            </ul>
          </section>
        </article>

        <section
          className="product-detail__information"
          aria-labelledby="product-information-title"
        >
          <div className="product-detail__description">
            <p className="product-detail__eyebrow">Flavor notes</p>
            <h2 id="product-information-title">About {product.name}</h2>
            {descriptions.map((paragraph, index) => (
              <p key={`${product.id}-description-${index}`}>{paragraph}</p>
            ))}

            {features.length > 0 && (
              <div className="product-detail__features">
                <h3>Recommended Uses &amp; Features</h3>
                <ul>
                  {features.map((feature, index) => (
                    <li key={`${product.id}-feature-${index}`}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="product-detail__more-information">
            <p className="product-detail__eyebrow">Product details</p>
            <h2>More Information</h2>
            <div className="product-detail__table-wrap">
              <table>
                <tbody>
                  {informationRows.map(([label, value]) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {relatedProducts.length > 0 && (
          <section
            className="product-detail__related"
            aria-labelledby="related-products-title"
          >
            <header>
              <p className="product-detail__eyebrow">Keep exploring</p>
              <h2 id="related-products-title">Related Products</h2>
            </header>
            <div className="product-detail__related-grid">
              {relatedProducts.map((relatedProduct) => (
                <article key={relatedProduct.id}>
                  <Link
                    className="product-detail__related-image-link"
                    to={`/shopDetails/${relatedProduct.id}`}
                    aria-label={`View ${relatedProduct.name}`}
                  >
                    <ProductImage
                      product={relatedProduct}
                      className="product-detail__related-image"
                      ariaLabel={relatedProduct.name}
                    />
                  </Link>
                  <p>{relatedProduct.category}</p>
                  <h3>
                    <Link to={`/shopDetails/${relatedProduct.id}`}>
                      {relatedProduct.name}
                    </Link>
                  </h3>
                  <strong>{formatPrice(getProductPrice(relatedProduct))}</strong>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
